import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { chatAPI, filesAPI } from "../lib/api";
import { 
  Send, 
  Mic, 
  MicOff, 
  Volume2, 
  VolumeX, 
  Loader2, 
  Bot, 
  User, 
  Trash2,
  Play,
  Pause,
  Square,
  FileText,
  Image,
  Music,
  Video,
  File,
  ChevronDown,
  ChevronUp,
  Paperclip,
  X,
  CheckCircle2,
  AlertCircle,
  Upload,
  Cpu,
  FolderPlus,
  Globe
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const sourceTypeIcons = {
  image: Image,
  document: FileText,
  audio: Music,
  video: Video,
  other: File
};

function SourcesCitation({ sources, expanded, onToggle }) {
  if (!sources || sources.length === 0) return null;
  
  // Deduplicate sources by file_id
  const uniqueSources = [];
  const seenFiles = new Set();
  for (const src of sources) {
    if (!seenFiles.has(src.file_id)) {
      seenFiles.add(src.file_id);
      uniqueSources.push(src);
    }
  }

  return (
    <div className="mt-2 border-t border-gray-200 pt-2">
      <button 
        onClick={onToggle}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <FileText className="w-3 h-3" />
        <span>{uniqueSources.length} source{uniqueSources.length !== 1 ? 's' : ''} cited</span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {sources.map((src, i) => {
            const IconComp = sourceTypeIcons[src.file_type] || sourceTypeIcons.other;
            return (
              <div key={i} className="bg-white/60 rounded border border-gray-200 p-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <IconComp className="w-3 h-3 text-primary" />
                  <span className="text-xs font-medium text-foreground truncate">{src.filename}</span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0 ml-auto">
                    {Math.round(src.relevance * 100)}% match
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug line-clamp-3">
                  {src.passage}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AIChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  
  // Voice playback state
  const [playingMessageIdx, setPlayingMessageIdx] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [expandedSources, setExpandedSources] = useState({});
  
  // File upload state
  const [uploading, setUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // {id, name, uploadStatus, embeddingStatus}
  const [isDragging, setIsDragging] = useState(false);
  const [recentFileIds, setRecentFileIds] = useState([]); // recently uploaded file IDs for priority search
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const pollIntervalRef = useRef(null);
  
  // Save as Project state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [savingProject, setSavingProject] = useState(false);
  
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize with a welcome message
  useEffect(() => {
    setMessages([{
      role: "assistant",
      content: "Hello! I'm Archiva AI, your intelligent archive assistant. I can help you:\n\n- Search and find files in your archive\n- Summarize document content\n- Create abstracts and insights\n- Answer questions about your stored files\n\nYou can type your questions or use the microphone button to speak. How can I help you today?"
    }]);
  }, []);

  // Poll embedding status for pending files
  useEffect(() => {
    const pendingIds = pendingFiles
      .filter(f => f.id && f.embeddingStatus !== "completed" && f.embeddingStatus !== "failed" && f.embeddingStatus !== "skipped" && f.embeddingStatus !== "disabled" && f.uploadStatus !== "error")
      .map(f => f.id);
    
    if (pendingIds.length === 0) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const res = await filesAPI.batchStatus(pendingIds);
        const statusMap = {};
        for (const s of res.data.statuses) {
          statusMap[s.id] = { embeddingStatus: s.embedding_status, hasText: s.has_text, fileType: s.file_type, embeddingError: s.embedding_error };
        }
        setPendingFiles(prev => prev.map(f => {
          if (f.id && statusMap[f.id]) {
            return { ...f, ...statusMap[f.id] };
          }
          return f;
        }));
      } catch {}
    };

    poll();
    pollIntervalRef.current = setInterval(poll, 3000);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [pendingFiles.map(f => `${f.id}:${f.embeddingStatus}`).join(",")]);

  // Auto-dismiss fully completed files after 5s (but keep failed ones with retry)
  useEffect(() => {
    const allDone = pendingFiles.length > 0 && pendingFiles.every(f =>
      f.uploadStatus === "error" || f.embeddingStatus === "completed" || f.embeddingStatus === "failed" || f.embeddingStatus === "skipped" || f.embeddingStatus === "disabled"
    );
    const hasRetryable = pendingFiles.some(f => f.embeddingStatus === "failed" && f.hasText !== false);
    if (allDone && !hasRetryable) {
      const timer = setTimeout(() => setPendingFiles([]), 5000);
      return () => clearTimeout(timer);
    }
  }, [pendingFiles]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = "";
    
    setUploading(true);
    const initialPending = files.map(f => ({ id: null, name: f.name, uploadStatus: "uploading", embeddingStatus: null }));
    setPendingFiles(prev => [...prev, ...initialPending]);
    const offset = pendingFiles.length; // offset for indexing into the new batch
    
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const formData = new FormData();
        formData.append("file", files[i]);
        const res = await filesAPI.upload(formData);
        const fileId = res.data.id;
        uploaded.push({ id: fileId, name: files[i].name });
        setPendingFiles(prev => prev.map((pf, idx) => 
          idx === offset + i ? { ...pf, id: fileId, uploadStatus: "done", embeddingStatus: res.data.embedding_status || "pending" } : pf
        ));
      } catch {
        setPendingFiles(prev => prev.map((pf, idx) => 
          idx === offset + i ? { ...pf, uploadStatus: "error" } : pf
        ));
      }
    }
    
    setUploading(false);
    
    if (uploaded.length > 0) {
      const names = uploaded.map(u => u.name).join(", ");
      const newIds = uploaded.map(u => u.id);
      setRecentFileIds(prev => [...prev, ...newIds]);
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} uploaded`);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `I've added ${uploaded.length} new file${uploaded.length > 1 ? "s" : ""} to your archive: ${names}. They are now being indexed for search — watch the status chips below.`
      }]);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const files = e.dataTransfer.files;
    if (files?.length) handleFileUpload({ target: { files, value: "" } });
  };

  const sendMessage = async (text = input) => {
    if (!text.trim()) return;
    
    const userMessage = { role: "user", content: text.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const res = await chatAPI.send(text.trim(), sessionId, true, recentFileIds.length > 0 ? recentFileIds : null);
      const assistantMessage = { 
        role: "assistant", 
        content: res.data.response,
        sources: res.data.sources || [],
        webSearchUsed: res.data.web_search_used || false
      };
      setMessages(prev => [...prev, assistantMessage]);
      setSessionId(res.data.session_id);

      // Auto-speak response if enabled
      if (autoSpeak) {
        // Get the index of the new message (current messages + user message + this message)
        const newMsgIdx = messages.length + 1;
        setTimeout(() => speakText(res.data.response, newMsgIdx), 100);
      }
    } catch (err) {
      console.error("Chat error:", err);
      toast.error("Failed to get response");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I encountered an error. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Voice Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info("Recording... Click again to stop");
    } catch (err) {
      console.error("Recording error:", err);
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setTranscribing(true);
    try {
      const res = await chatAPI.speechToText(audioBlob);
      const transcribedText = res.data.text;
      if (transcribedText) {
        setInput(transcribedText);
        // Auto-send after transcription
        sendMessage(transcribedText);
      } else {
        toast.warning("Could not transcribe audio");
      }
    } catch (err) {
      console.error("Transcription error:", err);
      toast.error("Failed to transcribe audio");
    } finally {
      setTranscribing(false);
    }
  };

  // Text-to-Speech with better controls
  const speakText = async (text, messageIdx) => {
    // If clicking on a different message while one is playing, stop current and start new
    if (playingMessageIdx !== null && playingMessageIdx !== messageIdx) {
      stopSpeaking();
    }
    
    // If this message is already playing, toggle pause/resume
    if (playingMessageIdx === messageIdx && audioRef.current) {
      if (isPaused) {
        audioRef.current.play();
        setIsPaused(false);
      } else {
        audioRef.current.pause();
        setIsPaused(true);
      }
      return;
    }

    setIsLoadingAudio(true);
    setPlayingMessageIdx(messageIdx);
    setIsPaused(false);
    
    try {
      const res = await chatAPI.textToSpeech(text.substring(0, 4096), "nova", 1.0);
      const audioData = res.data.audio_base64;
      
      // Create audio element and play
      const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingMessageIdx(null);
        setIsPaused(false);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        setPlayingMessageIdx(null);
        setIsPaused(false);
        audioRef.current = null;
        toast.error("Failed to play audio");
      };
      
      setIsLoadingAudio(false);
      await audio.play();
    } catch (err) {
      console.error("TTS error:", err);
      toast.error("Failed to generate speech");
      setPlayingMessageIdx(null);
      setIsPaused(false);
      setIsLoadingAudio(false);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlayingMessageIdx(null);
    setIsPaused(false);
  };

  const stopAllAudio = () => {
    stopSpeaking();
    toast.info("Audio stopped");
  };

  const clearChat = () => {
    stopSpeaking();
    setMessages([{
      role: "assistant",
      content: "Chat cleared. How can I help you?"
    }]);
    if (sessionId) {
      chatAPI.clearSession(sessionId).catch(() => {});
    }
    setSessionId(null);
  };

  const openSaveDialog = () => {
    if (!sessionId || messages.length <= 1) {
      toast.error("No conversation to save. Start chatting first!");
      return;
    }
    setProjectName("");
    setProjectDescription("");
    setShowSaveDialog(true);
  };

  const saveAsProject = async () => {
    if (!projectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    
    setSavingProject(true);
    try {
      const res = await chatAPI.saveAsProject(sessionId, projectName.trim(), projectDescription.trim());
      toast.success(`Saved as project "${res.data.project_name}" with ${res.data.messages_copied} messages`);
      setShowSaveDialog(false);
      // Clear chat since session was moved to project
      setMessages([{
        role: "assistant",
        content: `Your conversation has been saved as the project "${res.data.project_name}". You can continue working on it in the Projects section. How can I help you with something new?`
      }]);
      setSessionId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save as project");
    } finally {
      setSavingProject(false);
    }
  };

  return (
    <div
      className="space-y-4 fade-in h-[calc(100vh-120px)] flex flex-col relative"
      data-testid="ai-chat-page"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-primary/5 border-2 border-dashed border-primary rounded-xl flex items-center justify-center backdrop-blur-sm pointer-events-none">
          <div className="text-center">
            <Paperclip className="w-10 h-10 text-primary mx-auto mb-2" />
            <p className="text-lg font-semibold text-primary">Drop files to upload</p>
            <p className="text-sm text-muted-foreground">Files will be embedded for AI search</p>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight mb-1 sm:mb-2">AI Archivist</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Chat with AI about your archived files</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Global Stop Button - shown when any audio is playing */}
          {playingMessageIdx !== null && (
            <Button
              variant="destructive"
              size="sm"
              onClick={stopAllAudio}
              className="gap-2"
              data-testid="stop-all-audio-btn"
            >
              <Square className="w-4 h-4" />
              Stop Audio
            </Button>
          )}
          <Button
            variant={autoSpeak ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoSpeak(!autoSpeak)}
            className="gap-2"
            data-testid="auto-speak-toggle"
          >
            {autoSpeak ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            Auto-speak
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearChat}
            className="gap-2"
            data-testid="clear-chat-btn"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openSaveDialog}
            className="gap-2"
            data-testid="save-as-project-btn"
          >
            <FolderPlus className="w-4 h-4" />
            Save as Project
          </Button>
        </div>
      </div>

      {/* Chat Messages */}
      <Card className="flex-1 overflow-hidden border border-border">
        <ScrollArea className="h-full p-4">
          <div className="space-y-4 pb-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-message-${idx}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                )}
                <div
                  className={`max-w-[90%] sm:max-w-[80%] rounded-lg px-3 sm:px-4 py-2 sm:py-3 ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  } ${playingMessageIdx === idx ? "ring-2 ring-primary/50" : ""}`}
                >
                  {msg.role === "assistant" && msg.webSearchUsed && (
                    <div className="flex items-center gap-1 mb-2">
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        <Globe className="w-3 h-3 mr-1" />
                        Web Search
                      </Badge>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-1 mt-2">
                      {/* Play/Pause Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 px-2 text-xs ${playingMessageIdx === idx ? "opacity-100" : "opacity-60 hover:opacity-100"}`}
                        onClick={() => speakText(msg.content, idx)}
                        disabled={isLoadingAudio && playingMessageIdx === idx}
                        data-testid={`speak-message-${idx}`}
                      >
                        {isLoadingAudio && playingMessageIdx === idx ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Loading...
                          </>
                        ) : playingMessageIdx === idx ? (
                          isPaused ? (
                            <>
                              <Play className="w-3 h-3 mr-1" />
                              Resume
                            </>
                          ) : (
                            <>
                              <Pause className="w-3 h-3 mr-1" />
                              Pause
                            </>
                          )
                        ) : (
                          <>
                            <Play className="w-3 h-3 mr-1" />
                            Listen
                          </>
                        )}
                      </Button>
                      {/* Stop Button - only shown for the playing message */}
                      {playingMessageIdx === idx && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                          onClick={stopSpeaking}
                          data-testid={`stop-message-${idx}`}
                        >
                          <Square className="w-3 h-3 mr-1" />
                          Stop
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Sources Citation */}
                  {msg.role === "assistant" && msg.sources && msg.sources.length > 0 && (
                    <SourcesCitation 
                      sources={msg.sources} 
                      expanded={!!expandedSources[idx]}
                      onToggle={() => setExpandedSources(prev => ({...prev, [idx]: !prev[idx]}))}
                    />
                  )}
                </div>
                {msg.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
                <div className="bg-muted rounded-lg px-4 py-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </Card>

      {/* Pending Files with Embedding Status */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="embedding-status-bar">
          {pendingFiles.map((f, i) => {
            let chipColor = "bg-blue-100 text-blue-700 border-blue-200";
            let icon = <Loader2 className="w-3 h-3 animate-spin" />;
            let label = "Uploading...";
            let showRetry = false;
            
            if (f.uploadStatus === "error") {
              chipColor = "bg-red-100 text-red-700 border-red-200";
              icon = <AlertCircle className="w-3 h-3" />;
              label = "Upload failed";
            } else if (f.uploadStatus === "done") {
              if (f.embeddingStatus === "completed") {
                chipColor = "bg-green-100 text-green-700 border-green-200";
                icon = <CheckCircle2 className="w-3 h-3" />;
                label = "Ready";
              } else if (f.embeddingStatus === "processing") {
                chipColor = "bg-amber-100 text-amber-700 border-amber-200";
                icon = <Cpu className="w-3 h-3 animate-pulse" />;
                label = "Embedding...";
              } else if (f.embeddingStatus === "failed") {
                chipColor = "bg-red-100 text-red-700 border-red-200";
                icon = <AlertCircle className="w-3 h-3" />;
                label = f.embeddingError || (f.hasText === false ? "No text to index" : "Embedding failed");
                showRetry = f.hasText !== false;
              } else if (f.embeddingStatus === "skipped") {
                chipColor = "bg-gray-100 text-gray-600 border-gray-200";
                icon = <FileText className="w-3 h-3" />;
                label = f.embeddingError || "No text to index";
              } else if (f.embeddingStatus === "disabled") {
                chipColor = "bg-gray-100 text-gray-600 border-gray-200";
                icon = <AlertCircle className="w-3 h-3" />;
                label = "AI not configured";
              } else {
                chipColor = "bg-amber-100 text-amber-700 border-amber-200";
                icon = <Loader2 className="w-3 h-3 animate-spin" />;
                label = "Queued...";
              }
            }
            
            const handleRetry = async () => {
              if (!f.id) return;
              try {
                await filesAPI.retryEmbedding(f.id);
                setPendingFiles(prev => prev.map((pf, idx) =>
                  idx === i ? { ...pf, embeddingStatus: "pending" } : pf
                ));
                toast.info(`Retrying embedding for ${f.name}`);
              } catch {
                toast.error("Retry failed");
              }
            };
            
            return (
              <span
                key={i}
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${chipColor}`}
                data-testid={`file-status-chip-${i}`}
              >
                {icon}
                <span className="truncate max-w-[120px]">{f.name}</span>
                <span className="opacity-70 text-[10px]">{label}</span>
                {showRetry && (
                  <button
                    onClick={handleRetry}
                    className="ml-0.5 underline text-[10px] font-medium hover:opacity-80"
                    data-testid={`retry-embedding-${i}`}
                  >
                    Retry
                  </button>
                )}
              </span>
            );
          })}
          <button
            onClick={() => setPendingFiles([])}
            className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
            title="Dismiss all"
            data-testid="dismiss-status-chips"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className="flex gap-2">
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileUpload}
          className="hidden"
          data-testid="chat-file-input"
        />
        <Button
          variant="outline"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || loading}
          className="flex-shrink-0"
          title="Attach files for embedding"
          data-testid="chat-attach-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </Button>
        <Button
          variant={isRecording ? "destructive" : "outline"}
          size="icon"
          onClick={toggleRecording}
          disabled={transcribing || loading}
          className="flex-shrink-0"
          data-testid="voice-input-btn"
        >
          {transcribing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isRecording ? (
            <MicOff className="w-4 h-4" />
          ) : (
            <Mic className="w-4 h-4" />
          )}
        </Button>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={transcribing ? "Transcribing..." : "Type your message or use voice..."}
          disabled={loading || transcribing}
          className="flex-1"
          data-testid="chat-input"
        />
        <Button
          onClick={() => sendMessage()}
          disabled={!input.trim() || loading}
          className="flex-shrink-0"
          data-testid="send-message-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Save as Project Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Conversation as Project</DialogTitle>
            <DialogDescription>
              Your conversation will be saved as a new project. You can continue the discussion 
              and add files in the Projects section.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name *</Label>
              <Input
                id="project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name..."
                data-testid="project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-description">Description (optional)</Label>
              <Textarea
                id="project-description"
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder="What is this project about?"
                rows={3}
                data-testid="project-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveAsProject} 
              disabled={savingProject || !projectName.trim()}
              data-testid="confirm-save-project-btn"
            >
              {savingProject ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Save as Project
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
